import React, { useMemo, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PlayerDetailView = ({ player, allGames, onBack, onHome, onLogout }) => {
    const [activeTab, setActiveTab] = useState('overview');

    const playerGames = useMemo(() => {
        return allGames.filter(game => game.players.some(p => p.name === player.name));
    }, [player, allGames]);

    const scoreProgressionData = useMemo(() => {
        return playerGames.map((game, index) => ({
            game: `G${index + 1}`,
            score: game.players.find(p => p.name === player.name).score,
        }));
    }, [playerGames, player.name]);

    const bidsVsWonData = useMemo(() => {
        const data = [];
        playerGames.forEach((game, gameIndex) => {
            let totalBids = 0;
            let totalWon = 0;
            game.handHistory.forEach(hand => {
                const playerData = hand.players[player.name];
                if (playerData) {
                    if (playerData.bid !== 'nil' && playerData.bid !== 'blind-nil') {
                        totalBids += Number(playerData.bid);
                    }
                    totalWon += playerData.won;
                }
            });
            data.push({ game: `G${gameIndex + 1}`, Bids: totalBids, Won: totalWon });
        });
        return data;
    }, [playerGames, player.name]);

    const stats = useMemo(() => {
        const initialStats = {
            gamesPlayed: playerGames.length,
            wins: playerGames.filter(game => game.winner && game.winner.name === player.name).length,
            totalScore: playerGames.reduce((acc, game) => acc + game.players.find(p => p.name === player.name).score, 0),
            totalBags: playerGames.reduce((acc, game) => acc + game.players.find(p => p.name === player.name).bags, 0),
            highestScore: playerGames.length > 0 ? Math.max(...playerGames.map(game => game.players.find(p => p.name === player.name).score)) : 0,
            placements: { 1: 0, 2: 0, 3: 0, 4: 0 }
        };

        playerGames.forEach(game => {
            const sortedPlayers = [...game.players].sort((a, b) => b.score - a.score);
            const rank = sortedPlayers.findIndex(p => p.name === player.name) + 1;
            if (rank >= 1 && rank <= 4) {
                initialStats.placements[rank]++;
            }
        });

        return {
            ...initialStats,
            winRate: initialStats.gamesPlayed > 0 ? ((initialStats.wins / initialStats.gamesPlayed) * 100).toFixed(0) : 0,
            averageScore: initialStats.gamesPlayed > 0 ? (initialStats.totalScore / initialStats.gamesPlayed).toFixed(0) : 0,
            bagsPerGame: initialStats.gamesPlayed > 0 ? (initialStats.totalBags / initialStats.gamesPlayed).toFixed(1) : 0
        };
    }, [playerGames, player.name]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;

        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };


    return (
        <div className="h-screen bg-[#1a2332] text-white font-['Inter'] flex flex-col items-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            <header className="relative z-20 w-full max-w-7xl p-4 md:p-6 flex flex-col gap-4 md:gap-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 md:gap-4">
                        <button
                            onClick={onBack}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-black text-sm md:text-base shadow-lg shadow-amber-500/20">
                                {player.name.substring(0, 2).toUpperCase()}
                            </div>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight font-['Roboto_Slab'] text-white uppercase truncate max-w-[150px] md:max-w-none">
                                {player.name}
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={onHome}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Home"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                        </button>
                        <button
                            onClick={onLogout}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Logout"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="flex gap-2 bg-white/5 p-1 rounded-xl w-full md:w-fit border border-white/10 overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`flex-1 md:flex-none px-4 md:px-6 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'overview' ? 'bg-amber-500 text-white shadow-lg' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 md:flex-none px-4 md:px-6 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'history' ? 'bg-amber-500 text-white shadow-lg' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        Match History
                    </button>
                </div>
            </header>

            <main className="relative z-10 w-full max-w-7xl flex-1 overflow-y-auto px-4 md:px-6 pb-6 custom-scrollbar">
                <div className="h-full bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl overflow-y-auto p-4 sm:p-6">
                    {activeTab === 'overview' && (
                        <>
                            {/* Key Stats Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-5">
                                    <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Win Rate</p>
                                    <p className="text-2xl md:text-3xl font-black text-amber-400">{stats.winRate}%</p>
                                    <p className="text-[10px] md:text-xs text-gray-500 mt-1">{stats.wins} wins / {stats.gamesPlayed} games</p>
                                </div>
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-5">
                                    <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Avg Score</p>
                                    <p className="text-2xl md:text-3xl font-black text-white">{stats.averageScore}</p>
                                    <p className="text-[10px] md:text-xs text-gray-500 mt-1">High: {stats.highestScore}</p>
                                </div>
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-5">
                                    <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Total Bags</p>
                                    <p className="text-2xl md:text-3xl font-black text-red-400">{stats.totalBags}</p>
                                    <p className="text-[10px] md:text-xs text-gray-500 mt-1">{stats.bagsPerGame} per game</p>
                                </div>
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-5">
                                    <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Best Rank</p>
                                    <p className="text-2xl md:text-3xl font-black text-yellow-400">#{Object.keys(stats.placements).find(k => stats.placements[k] > 0) || '-'}</p>
                                    <p className="text-[10px] md:text-xs text-gray-500 mt-1">Most often: #{Object.entries(stats.placements).sort((a, b) => b[1] - a[1])[0][0]}</p>
                                </div>
                            </div>

                            {/* Charts */}
                            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-6">
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-6">
                                    <h3 className="font-bold text-sm uppercase tracking-wider mb-4 text-gray-300">Score Progression</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={scoreProgressionData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis dataKey="game" stroke="#94A3B8" tick={{ fontSize: 10 }} />
                                            <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                            <Line type="monotone" dataKey="score" stroke="#fbbf24" strokeWidth={2} dot={{ fill: '#fbbf24' }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="bg-[#1e293b]/60 backdrop-blur-md rounded-2xl border border-white/10 p-4 md:p-6">
                                    <h3 className="font-bold text-sm uppercase tracking-wider mb-4 text-gray-300">Bids vs Won (Total per Game)</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={bidsVsWonData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis dataKey="game" stroke="#94A3B8" tick={{ fontSize: 10 }} />
                                            <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="Bids" fill="#818cf8" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="Won" fill="#34d399" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'history' && (
                        <div className="relative">
                            {/* Timeline Line */}
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/50 via-amber-500/20 to-transparent" />

                            <div className="space-y-6">
                                {playerGames.map((game, index) => {
                                    const sortedPlayers = [...game.players].sort((a, b) => b.score - a.score);
                                    const rank = sortedPlayers.findIndex(p => p.name === player.name) + 1;
                                    const playerData = game.players.find(p => p.name === player.name);
                                    const isWin = rank === 1;

                                    return (
                                        <div key={index} className="relative pl-12 group">
                                            {/* Timeline Dot */}
                                            <div className={`absolute left-2 top-4 w-4 h-4 rounded-full border-2 border-[#1e293b] ${isWin ? 'bg-amber-500 shadow-lg shadow-amber-500/50' : 'bg-gray-600'
                                                } group-hover:scale-125 transition-transform z-10`} />

                                            <div className="w-full bg-black/20 rounded-2xl border border-white/5 overflow-hidden hover:border-amber-500/30 hover:shadow-lg hover:shadow-amber-500/10 transition-all">
                                                <div className="p-4 flex items-center justify-between">
                                                    {/* Left: Rank & Date */}
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-lg font-black ${isWin ? 'text-amber-400' : 'text-white'}`}>
                                                                {rank === 1 ? '1st Place' : rank === 2 ? '2nd Place' : rank === 3 ? '3rd Place' : `${rank}th Place`}
                                                            </span>
                                                            {isWin && <span className="text-lg">🏆</span>}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            <span className="font-bold text-gray-400">{formatDate(game.date)}</span>
                                                            <span>•</span>
                                                            <span>{getTimeAgo(game.date)}</span>
                                                        </div>
                                                    </div>

                                                    {/* Right: Score & Bags */}
                                                    <div className="flex items-center gap-4 md:gap-6">
                                                        {playerData.bags > 0 && (
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Bags</span>
                                                                <span className="text-sm font-bold text-red-400">{playerData.bags} 💼</span>
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Score</span>
                                                            <span className="text-2xl font-black font-mono text-white">{playerData.score}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default PlayerDetailView;
